// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { snakeCase, kebabCase } from 'lodash';
import {
  requestAddToCart,
  requestRemoveFromCart,
  requestAddToPublicCart,
} from 'services/actions/cartActions';
import { displaySponsoredPopupWarning } from 'services/actions/productsActions';

import { formatCurrencyValue, convertToCents } from 'utils/currency';
import {
  sortGwpByThresholdDesc,
  nextReachedGwpProducts,
  differenceWithGwpInCart,
  isThereABetterMatching,
} from 'utils/gwp/thresholdCalculations';
import {
  gwpWarningPopUpBuilder,
  mapDifferenceToPopUpBuilder,
} from 'utils/gwp/popUps';
import { updateLocationSearch } from 'utils/url';
import { btnLocationEnum } from 'utils/segment';
import { VIEW_SIZE } from 'utils/device';
import env from 'utils/env';
import {
  getCadenceType,
  PRODUCT_PURCHASE_OPTIONS,
  SEGMENT_PRODUCT_TYPE,
} from 'utils/subscriptions';

import withVersion from 'HOC/withVersion/withVersion';
import withSelectedCategoryId from 'HOC/withSelectedCategoryId/withSelectedCategoryId';

import { selectGwpProductsInCart } from 'selectors/cartSelectors';

import ToolTip from 'shared-components/ToolTip/ToolTip';
import Button, { BUTTON_TYPES } from 'shared-components/Button/Button';
import QuantityButton from '../QuantityButton/QuantityButton';

import styles from './UpdateCartButton.module.css';

import type {
  State,
  BundledVariant,
  BundledProduct,
  StateProductData,
} from '../../../sharedTypes';

type OwnProps = {|
  +productName: string,
  +isGwp: boolean,
  +sponsored: boolean,
  +sponsoredThreshold: number,
  +hasSponsoredProductInCart: boolean,
  +subtotal: number,
  +viewSize: string,
  +buttonLocation:
    | btnLocationEnum.productAccordion
    | btnLocationEnum.cart
    | btnLocationEnum.productCard,
  +positionLocation?: string,
  +referrerProductId: string | null,
  +positionOrder?: number,
  +variant: { ...BundledVariant } | null,
  +nonColorVariant?: boolean,
  +isColorOnlyVariant?: boolean,
  +isMultipleVariant?: boolean,
  +shareUrl?: string,
  +overwriteHandleClickAdd?: (any) => any,
  +overwriteHandleClickRemove?: (any) => any,
  +gwpProducts?: Array<BundledProduct>,
  +productPurchaseOption?: string,
  +hasActiveSubscription?: boolean,
|};

type InjectedProps = {|
  +version: string,
  +selectedCategoryId: string | null,
|};

type DispatchProps = {|
  +mapDifferenceToPopUp: Function,
  +gwpWarningPopUp: Function,
  +requestAddToCart: typeof requestAddToCart,
  +requestRemoveFromCart: typeof requestRemoveFromCart,
  +displaySponsoredPopupWarning: typeof displaySponsoredPopupWarning,
|};

type StateProps = {|
  +isShippingWithBox: boolean,
  +gwpInCart: StateProductData,
  +loggedIn: boolean,
|};

type Props = {|
  ...DispatchProps,
  ...StateProps,
  ...InjectedProps,
  ...OwnProps,
|};

const getCartLimit = (
  quantity: number,
  isSubscribableShopVariant: boolean,
  cadenceType: string | null,
  subscription: any,
  cartLimit: number
): number => {
  // return subscription.cartLimit if quantity = 0 AND variant is subscribable
  // OR quantity > 0 AND variantCadenceTypes[id] exists (subscription in cart)
  if (
    (quantity === 0 && isSubscribableShopVariant) ||
    (quantity > 0 && cadenceType)
  )
    return subscription.cartLimit;
  return cartLimit;
};

function UpdateCartButton({
  productName,
  isGwp,
  sponsored,
  sponsoredThreshold,
  hasSponsoredProductInCart,
  subtotal,
  version,
  viewSize,
  nonColorVariant,
  isColorOnlyVariant,
  isMultipleVariant,
  buttonLocation,
  shareUrl,
  requestAddToCart,
  requestRemoveFromCart,
  displaySponsoredPopupWarning,
  isShippingWithBox,
  variant,
  selectedCategoryId,
  overwriteHandleClickAdd,
  overwriteHandleClickRemove,
  gwpProducts,
  gwpInCart,
  mapDifferenceToPopUp,
  gwpWarningPopUp,
  positionLocation,
  referrerProductId,
  positionOrder,
  accordionInGrid,
  showNoCheckoutRequiredModal,
  loggedIn,
  productPurchaseOption,
  hasActiveSubscription,
  requestAddToPublicCart,
}: Props) {
  if (!variant) {
    // displays the Not Available CTA
    return (
      <Button
        buttonType={BUTTON_TYPES.PRIMARY}
        className={styles.notAvailableBtn}
        disabled
      >
        Not Available
      </Button>
    );
  }

  const {
    areCartOperationsDisabled,
    quantity,
    cartLimit,
    inventoryLimit,
    isPreviouslyPurchased,
    isVariantAtPurchaseLimit,
    soldOut, // one-time purchase sold out
    quantityUpdating,
    isSoldOutOnFetch,
    id,
    parentId,
    price,
    salePrice,
    isSubscribableShopVariant,
    isSubscriptionAvailable,
    subscription,
    cadenceTypeInCart,
  } = variant;
  const sortedGwps = sortGwpByThresholdDesc(gwpProducts);
  const isAccordion = buttonLocation === btnLocationEnum.productAccordion;
  const hasReachedSponsoredThreshold = subtotal >= sponsoredThreshold;
  const showCartCTA = !isShippingWithBox;
  const isMobile = viewSize === VIEW_SIZE.small;
  const buttonName = showCartCTA ? 'ADD TO CART' : 'ADD TO BOX';
  const buttonNameDuringLoading = !isGwp
    ? isMobile
      ? 'ADDING TO'
      : showCartCTA
      ? 'ADDING TO CART...'
      : 'ADDING TO BOX...'
    : 'ADDING FREE GIFT...';

  const subscriptionSoldOut = !isSubscriptionAvailable || hasActiveSubscription;

  const getIsSoldOut = () => {
    // the selected purchase option is save & subscribe
    // we return the subscription available status from subscription data
    if (productPurchaseOption === PRODUCT_PURCHASE_OPTIONS.SAVE_SUBSCRIBE) {
      return subscriptionSoldOut;
    }
    // the selected purchase option is one time purchase
    // we return the sold out value from one off variant data
    if (productPurchaseOption === PRODUCT_PURCHASE_OPTIONS.ONE_TIME) {
      return soldOut;
    }
    // the variant is OOS if one-time purchase is sold out + not subscribable
    // OR one-time purchase is sold out + subscribable + subscription is sold out
    // OR one-time purchase is sold out + subscribable + user has active subscription
    return soldOut && (!isSubscribableShopVariant || subscriptionSoldOut);
  };

  const isSoldOut = getIsSoldOut();

  const cadenceType = getCadenceType(
    quantity,
    cadenceTypeInCart,
    productPurchaseOption,
    isSubscriptionAvailable,
    hasActiveSubscription
  );
  const variantCartLimit = getCartLimit(
    quantity,
    isSubscribableShopVariant,
    cadenceType,
    subscription,
    cartLimit
  );

  const variantInventoryLimit =
    isSubscribableShopVariant &&
    productPurchaseOption === PRODUCT_PURCHASE_OPTIONS.SAVE_SUBSCRIBE
      ? subscription.inventoryLimit
      : inventoryLimit;

  const dataTestId = `${kebabCase(buttonName)}-${kebabCase(
    buttonLocation
  )}-btn`;

  function defaultHandleClickAdd() {
    if (loggedIn) {
      if (showNoCheckoutRequiredModal) {
        showNoCheckoutRequiredModal();
      }
      requestAddToCart(
        id,
        parentId,
        buttonLocation,
        buttonName,
        selectedCategoryId,
        positionLocation,
        referrerProductId,
        positionOrder,
        cadenceType
      );
    } else {
      const variantSku = variant.sku;
      const redirectUrl = `https://${env.hostname}/checkout?sku=${variantSku}`;
      requestAddToPublicCart(
        id,
        buttonLocation,
        cadenceType,
        variantSku,
        productName,
        salePrice,
        redirectUrl
      );
    }
  }

  const defaultAction = () =>
    requestRemoveFromCart(
      id,
      parentId,
      buttonLocation,
      buttonName,
      selectedCategoryId,
      positionLocation,
      referrerProductId,
      positionOrder,
      cadenceType
    );

  const displaySponsoredWarning = () =>
    isGwp ? gwpWarningPopUp(id) : displaySponsoredPopupWarning(id);

  function defaultHandleClickRemove(buttonName) {
    const finalPriceInCents = Math.min(
      convertToCents(price),
      convertToCents(salePrice)
    );
    const subtotalOnRemoval = subtotal - finalPriceInCents;
    const willFallBelowSponsoredThreshold =
      subtotalOnRemoval < sponsoredThreshold;
    if (gwpInCart && gwpInCart.gwpThreshold > subtotalOnRemoval) {
      const reachedProducts = nextReachedGwpProducts(
        subtotalOnRemoval,
        sortedGwps
      );
      const gwpDiferenceExpected = differenceWithGwpInCart(
        gwpInCart,
        reachedProducts,
        sortedGwps
      );
      const resolvedPopUp = mapDifferenceToPopUp(
        gwpDiferenceExpected,
        reachedProducts
      );
      if (resolvedPopUp) {
        resolvedPopUp(id);
      } else {
        defaultAction(id);
      }
    } else if (
      sponsored ||
      (hasSponsoredProductInCart &&
        hasReachedSponsoredThreshold &&
        willFallBelowSponsoredThreshold)
    ) {
      displaySponsoredWarning();
    } else {
      requestRemoveFromCart(
        id,
        parentId,
        buttonLocation,
        buttonName,
        selectedCategoryId,
        positionLocation,
        referrerProductId,
        positionOrder,
        cadenceType
      );
    }
  }

  const handleClickAdd = overwriteHandleClickAdd || defaultHandleClickAdd;
  const handleClickRemove =
    overwriteHandleClickRemove || defaultHandleClickRemove;

  // The order of these conditions is important, and should only
  // be changed very deliberately.
  // TODO: refactor this component to simplify the conditional logic

  if (sponsored && quantity > 0) {
    return (
      <QuantityButton
        variantId={id}
        productId={parentId}
        soldOut={soldOut}
        isPreviouslyPurchased={isPreviouslyPurchased}
        viewSize={viewSize}
        quantity={quantity}
        onAdd={handleClickAdd}
        onRemove={handleClickRemove}
        max={variantCartLimit}
        inventoryLimit={inventoryLimit}
        quantityUpdating={quantityUpdating}
        isSponsored
        buttonLocation={buttonLocation}
        positionLocation={positionLocation}
        areCartOperationsDisabled={areCartOperationsDisabled}
        version={version}
        isGwp={isGwp}
      />
    );
  }

  if (sponsored && !hasReachedSponsoredThreshold && !soldOut) {
    const linkText = isGwp
      ? `SHOP $${formatCurrencyValue(sponsoredThreshold)}+ TO UNLOCK`
      : 'ADD MORE TO UNLOCK';
    const sponsoredStyles = classNames(
      styles.belowSponsoredThreshold,
      styles[viewSize]
    );
    const gwpStyles = classNames(
      styles.gwpBelowSponsoredThreshold,
      styles[viewSize],
      styles.gwp
    );
    return (
      <Link
        to={updateLocationSearch(
          {
            product_link: shareUrl,
            ...(referrerProductId && {
              referrer_product_id: referrerProductId,
            }),
            ...(positionOrder && {
              position_order: positionOrder,
            }),
          },
          { accordionInGrid }
        )}
        className={`${isGwp ? gwpStyles : sponsoredStyles}`}
      >
        <div className={classNames({ [styles.gwpWithLock]: isGwp })}>
          <div>{isGwp ? <i className="fas fa-lock" /> : ''}</div>
          <div
            className={`${isGwp ? styles.gwpUnlockText : styles.sponsoredText}`}
          >
            {linkText}
          </div>
        </div>
      </Link>
    );
  }

  const hasReachedThresholdAndAvailability =
    hasReachedSponsoredThreshold && quantity === 0 && !soldOut;

  if (
    isGwp &&
    hasReachedThresholdAndAvailability &&
    isThereABetterMatching(sortedGwps, id, subtotal, gwpInCart)
  ) {
    return (
      <button
        type="button"
        className={classNames(
          styles[viewSize],
          styles.addToBoxBtn,
          styles.gwp,
          styles.gwpWithLock
        )}
        disabled
      >
        <p>UPGRADED FREE GIFT</p>
      </button>
    );
  }

  if (sponsored && hasReachedThresholdAndAvailability) {
    const buttonText = isGwp
      ? quantityUpdating
        ? buttonNameDuringLoading
        : 'ADD FREE GIFT'
      : quantityUpdating
      ? buttonNameDuringLoading
      : 'FREE! ADD NOW';
    return (
      <button
        className={classNames(styles.addToBoxBtn, styles[viewSize], {
          [styles.gwp]: isGwp,
        })}
        onClick={() => handleClickAdd(buttonName)}
        disabled={areCartOperationsDisabled}
        type="button"
        data-dd-action-name={`${buttonText} button in ${buttonLocation}`}
      >
        <div className={styles.gwpWithLock}>
          <div>{buttonText}</div>
        </div>
      </button>
    );
  }

  if (quantity > 0 && !nonColorVariant) {
    return (
      <QuantityButton
        variantId={id}
        productId={parentId}
        soldOut={soldOut}
        isPreviouslyPurchased={isPreviouslyPurchased}
        viewSize={viewSize}
        quantity={quantity}
        onAdd={handleClickAdd}
        onRemove={handleClickRemove}
        max={variantCartLimit}
        inventoryLimit={variantInventoryLimit}
        quantityUpdating={quantityUpdating}
        buttonLocation={buttonLocation}
        areCartOperationsDisabled={areCartOperationsDisabled}
        version={version}
        isGwp={isGwp}
      />
    );
  }

  if (
    hasActiveSubscription &&
    productPurchaseOption === PRODUCT_PURCHASE_OPTIONS.SAVE_SUBSCRIBE
  ) {
    return (
      <button
        className={classNames(
          styles.soldOutBtn,
          styles.redesign,
          styles[viewSize]
        )}
        type="button"
      >
        Not Available
      </button>
    );
  }

  if (isSoldOut) {
    return (
      <ToolTip
        content="Sorry, this product is no longer available."
        enabled={!isSoldOutOnFetch}
        isAccordion={isAccordion}
      >
        <button
          className={classNames(styles.soldOutBtn, styles[viewSize], {
            [styles.gwp]: isGwp,
          })}
          type="button"
        >
          Out of stock
        </button>
      </ToolTip>
    );
  }

  if (
    nonColorVariant ||
    (isMultipleVariant && isColorOnlyVariant && isMobile)
  ) {
    const getButtonText = () => {
      if (isMultipleVariant) {
        if (isColorOnlyVariant && isMobile) {
          return 'CHOOSE COLOR';
        }
        return 'CHOOSE OPTION';
      }
      return 'SELECT OPTIONS';
    };
    const buttonText = getButtonText();
    return (
      <Link
        to={updateLocationSearch(
          {
            product_link: shareUrl,
            ...(referrerProductId && {
              referrer_product_id: referrerProductId,
            }),
            ...(positionOrder && {
              position_order: positionOrder,
            }),
          },
          { accordionInGrid }
        )}
        className={classNames(styles.addToBoxBtn, styles[viewSize])}
        data-dd-action-name={`${buttonText} button in ${buttonLocation}`}
      >
        {buttonText}
      </Link>
    );
  }

  if (!isVariantAtPurchaseLimit) {
    const CTA = quantityUpdating ? buttonNameDuringLoading : buttonName;
    return (
      <button
        id={`${id}_${snakeCase(buttonName)}_${snakeCase(buttonLocation)}`}
        className={classNames(styles.addToBoxBtn, styles[viewSize])}
        onClick={() => handleClickAdd(buttonName)}
        disabled={areCartOperationsDisabled}
        type="button"
        data-testid={dataTestId}
        data-dd-action-name={`${CTA} button in ${buttonLocation}`}
      >
        {CTA}
      </button>
    );
  }

  return (
    <ToolTip
      content="You have already met your cart limit."
      isAccordion={isAccordion}
    >
      <button
        className={classNames(styles.soldOutBtn, styles[viewSize])}
        type="button"
        data-dd-action-name={`ADD TO BOX button in ${buttonLocation}`}
      >
        ADD TO BOX
      </button>
    </ToolTip>
  );
}

UpdateCartButton.defaultProps = {
  sponsored: false,
  sponsoredThreshold: 0,
  hasSponsoredProductInCart: false,
  gwpProducts: [],
  accordionInGrid: true,
  nonColorVariant: false,
  isColorOnlyVariant: false,
  isMultipleVariant: false,
  productPurchaseOption: null,
  hasActiveSubscription: false,
};

// We should be mindful that every mapStateToProps gets called on every
// store update, and a separate UpdateCartButton instance (each of which is connected
// with a mapStateToProps) is rendered in every Product. If this starts to make a
// significant impact on performance, we should consider passing these props from ProductGrid,
// or implementing windowing.
function mapStateToProps(state: State): StateProps {
  return {
    loggedIn: state.userState.loggedIn,
    isShippingWithBox: state.cartState.isShippingWithBox,
    gwpInCart: selectGwpProductsInCart(state),
  };
}

const mapDispatchToProps = {
  gwpWarningPopUp: gwpWarningPopUpBuilder,
  mapDifferenceToPopUp: mapDifferenceToPopUpBuilder,
  requestAddToCart,
  requestRemoveFromCart,
  displaySponsoredPopupWarning,
  requestAddToPublicCart,
};

export default (compose(
  withVersion,
  withSelectedCategoryId,
  connect<Props, {| ...OwnProps, ...InjectedProps |}, StateProps, _, _, _>(
    mapStateToProps,
    mapDispatchToProps
  ),
  React.memo
)(UpdateCartButton): React.AbstractComponent<OwnProps>);
